import React, { useEffect, useState, useRef } from 'react';
import './CustomCursor.css';

const CustomCursor = () => {
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [dotPosition, setDotPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  const cursorRef = useRef({ x: -100, y: -100 });
  const dotRef = useRef({ x: -100, y: -100 });
  const targetX = useRef(-100);
  const targetY = useRef(-100);

  useEffect(() => {
    // Hide default cursor on body
    document.body.style.cursor = 'none';
    document.documentElement.style.cursor = 'none';
    
    // Add style to hide cursor on all elements
    const style = document.createElement('style');
    style.textContent = `
      * {
        cursor: none !important;
      }
      body, html {
        cursor: none !important;
      }
    `;
    document.head.appendChild(style);

    let animationId;
    let dotAnimationId;

    const animateCursor = () => {
      // Smooth follow for main cursor
      cursorRef.current.x += (targetX.current - cursorRef.current.x) * 0.2;
      cursorRef.current.y += (targetY.current - cursorRef.current.y) * 0.2;
      
      // Faster follow for dot
      dotRef.current.x += (targetX.current - dotRef.current.x) * 0.3;
      dotRef.current.y += (targetY.current - dotRef.current.y) * 0.3;
      
      setCursorPosition({ x: cursorRef.current.x, y: cursorRef.current.y });
      setDotPosition({ x: dotRef.current.x, y: dotRef.current.y });
      
      animationId = requestAnimationFrame(animateCursor);
    };

    const handleMouseMove = (e) => {
      targetX.current = e.clientX;
      targetY.current = e.clientY;
      if (!isVisible && e.clientX > 0 && e.clientY > 0) {
        setIsVisible(true);
      }
    };

    const handleMouseOver = (e) => {
      const interactiveElements = [
        'BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 
        '.auth-btn', '.auth-link', '.resend-link span',
        '.timeline-content', '.nav-link', '.logout-btn',
        '.tab-btn', '.add-btn', '.submit-btn', '.modal-close',
        '.hero-buttons a', '.btn-primary', '.btn-secondary', '.menu-icon',
        '.auth-card', '.auth-btn', '.glitch-text'
      ];
      
      let target = e.target;
      let shouldHover = false;
      
      for (const selector of interactiveElements) {
        if (target.closest?.(selector) || target.matches?.(selector)) {
          shouldHover = true;
          break;
        }
      }
      
      // Also check for class names
      if (target.classList) {
        const classList = Array.from(target.classList);
        if (classList.some(c => c.includes('btn') || c.includes('link') || c.includes('clickable'))) {
          shouldHover = true;
        }
      }
      
      setIsHovering(shouldHover);
    };

    const handleMouseOut = () => {
      setIsHovering(false);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    const handleMouseEnter = () => {
      if (targetX.current > 0 && targetY.current > 0) {
        setIsVisible(true);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    
    animateCursor();

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      if (animationId) cancelAnimationFrame(animationId);
      document.head.removeChild(style);
      document.body.style.cursor = '';
      document.documentElement.style.cursor = '';
    };
  }, [isVisible]);

  if (!isVisible && cursorPosition.x === -100) return null;

  return (
    <>
      <div 
        className="custom-cursor"
        style={{ 
          left: `${cursorPosition.x}px`, 
          top: `${cursorPosition.y}px`,
          transform: 'translate(-50%, -50%)',
          opacity: isVisible ? 1 : 0
        }}
        data-hover={isHovering}
      />
      <div 
        className="custom-cursor-dot"
        style={{ 
          left: `${dotPosition.x}px`, 
          top: `${dotPosition.y}px`,
          transform: 'translate(-50%, -50%)',
          opacity: isVisible ? 1 : 0
        }}
      />
    </>
  );
};

export default CustomCursor;